import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { AGENT_LABEL_META } from "@/components/AgentLabelBadge";

/* Admin → AI labels — how the Fresh agent-posture labeller is taught and how
 * it performs. One overview call (GET /api/fresh/labels/admin/overview):
 * job state + Gemini spend, label distribution, learned phrases, human
 * corrections that steer the prompt, latest verdicts, and the literal prompt
 * text the model receives. Shareholder-anchored page. */

interface LabelCount { label: string; count: number; avg_confidence: number | null }
interface KindCount { source_kind: string; count: number }
interface PhraseRow {
  id: string; phrase: string; label: string; hit_count: number;
  created_at: string | null; created_by: string | null;
  source: string | null; external_id: number | null; url: string | null;
}
interface CorrectionRow {
  id: string; source: string; external_id: number; url: string;
  previous_label: string | null; previous_evidence: string | null; previous_reason: string | null;
  new_label: string | null; confirmed: boolean; phrase: string | null;
  by: string | null; created_at: string | null;
}
interface VerdictRow {
  source: string; external_id: number; url: string; title: string | null;
  label: string; confidence: number | null; reason: string | null; evidence: string | null;
  source_kind: string; model: string | null; labelled_at: string | null; overridden_by: string | null;
}
interface JobState {
  enabled: boolean; gemini_configured: boolean; model: string; tick_minutes: number;
  batch_size: number; max_per_tick: number; backfill_hours: number; daily_budget: number;
  calls_today: number; fewshot_n: number;
  last_run: {
    finished_at?: string | null; duration_ms?: number | null; ok?: boolean | null;
    error?: string | null; result?: Record<string, unknown> | null;
  } | null;
}
interface Overview {
  job: JobState; total: number; labelled_24h: number; pending_24h: number;
  by_label: LabelCount[]; by_kind: KindCount[];
  human_overrides: number; corrections_count: number; confirmations_count: number;
  phrases: PhraseRow[]; corrections: CorrectionRow[]; recent: VerdictRow[];
  prompt_preview: string;
}

const POLL_MS = 30_000;

const LABEL_TEXT: Record<string, string> = {
  no_agents: AGENT_LABEL_META.no_agents.text,
  likely_agent: AGENT_LABEL_META.likely_agent.text,
  agents_welcome: AGENT_LABEL_META.agents_welcome.text,
  none: "Nothing relevant",
};
const LABEL_BAR: Record<string, string> = {
  no_agents: "bg-rose-500",
  likely_agent: "bg-orange-500",
  agents_welcome: "bg-emerald-500",
  none: "bg-slate-600",
};
const KIND_TEXT: Record<string, string> = {
  gemini: "Gemini", phrase: "Taught phrase", regex: "Pattern fallback", human: "Human",
};

function ago(s: string | null | undefined): string {
  if (!s) return "—";
  const secs = Math.max(0, (Date.now() - new Date(s).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  if (secs < 129600) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function LabelPill({ label }: { label: string | null }) {
  if (!label) return <span className="text-slate-600">—</span>;
  const meta = (AGENT_LABEL_META as Record<string, { badge: string; text: string }>)[label];
  if (!meta) {
    return (
      <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-slate-700 text-slate-200">
        {LABEL_TEXT[label] ?? label}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>
      {meta.text}
    </span>
  );
}

export function AdminLabels() {
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [retiring, setRetiring] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<Overview>("/api/fresh/labels/admin/overview");
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(
        e instanceof ApiError && e.status === 403
          ? "You don't have access to this page."
          : e instanceof Error ? e.message : "Failed to load",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  async function runNow() {
    setRunning(true);
    setRunMsg(null);
    try {
      const r = await api<{ result: Record<string, unknown> }>("/api/fresh/labels/admin/run", { method: "POST" });
      const res = r.result;
      setRunMsg(
        res.skipped
          ? `Skipped: ${String(res.skipped)}`
          : res.ok
            ? `Labelled ${String(res.candidates ?? 0)} listings · ${String(res.gemini_calls ?? 0)} Gemini calls · ${String(res.phrase_hits ?? 0)} phrase hits · ${String(res.fallbacks ?? 0)} fallbacks · ${String(res.seconds ?? "?")}s`
            : `Failed: ${String(res.error ?? "unknown")}`,
      );
      await load();
    } catch (e) {
      setRunMsg(e instanceof Error ? e.message : "Run failed");
    } finally {
      setRunning(false);
    }
  }

  async function retire(id: string) {
    setRetiring(id);
    try {
      await api(`/api/fresh/label-phrases/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not retire phrase");
    } finally {
      setRetiring(null);
    }
  }

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="AI labels"
        subtitle="How the Fresh agent-posture model is taught and how it performs — prompt, learned phrases, corrections and Gemini spend."
        actions={
          <>
            <button
              type="button"
              onClick={() => void runNow()}
              className="btn-primary !px-3.5 !py-2 !text-xs"
              disabled={running || !data?.job.enabled}
              title="Run one labelling pass now (same code path as the scheduler)"
            >
              {running ? "Running…" : "Run a pass now"}
            </button>
            <button
              type="button"
              onClick={() => { setLoading(true); void load(); }}
              className="btn-secondary !px-3.5 !py-2 !text-xs"
              disabled={loading}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </>
        }
      />

      {err && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">{err}</div>
      )}
      {runMsg && (
        <div className="card p-3 text-[12.5px] text-slate-300">{runMsg}</div>
      )}

      {!data && !err && (
        <div className="space-y-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      )}

      {data && (
        <>
          {/* ── Job state ─────────────────────────────────────────────── */}
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                    !data.job.enabled || !data.job.gemini_configured
                      ? "bg-slate-500"
                      : data.job.last_run?.ok === false
                        ? "bg-rose-400"
                        : "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,.7)]"
                  }`}
                />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-slate-100">
                    {!data.job.enabled ? "Labelling is off" : !data.job.gemini_configured ? "Gemini key missing" : "Labelling every " + data.job.tick_minutes + " min"}
                  </div>
                  <div className="text-[11.5px] text-slate-500 truncate">
                    Model <span className="text-slate-300 font-mono">{data.job.model}</span>
                    {" · "}batches of {data.job.batch_size}{" · "}up to {data.job.max_per_tick} listings per pass{" · "}
                    window {data.job.backfill_hours}h
                  </div>
                </div>
              </div>
              <div className="text-right text-[11.5px] text-slate-500 shrink-0">
                last pass {ago(data.job.last_run?.finished_at)}
                {data.job.last_run?.duration_ms != null && ` · ${(data.job.last_run.duration_ms / 1000).toFixed(1)}s`}
                {data.job.last_run?.error && (
                  <div className="text-rose-400 truncate max-w-[22rem]">{data.job.last_run.error}</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 border-t border-white/5 [&>*]:border-white/5 sm:[&>*:not(:first-child)]:border-l">
              <Stat label="Labelled total" value={data.total.toLocaleString()} />
              <Stat label="Labelled · 24h" value={data.labelled_24h.toLocaleString()} sub={data.pending_24h > 0 ? `${data.pending_24h} waiting` : "queue empty"} />
              <Stat
                label="Gemini calls today"
                value={`${data.job.calls_today} / ${data.job.daily_budget}`}
                sub="hard daily cap"
                warn={data.job.calls_today > data.job.daily_budget * 0.8}
              />
              <Stat label="Human overrides" value={data.human_overrides.toLocaleString()} sub={`${data.corrections_count} corrections · ${data.confirmations_count} confirmed`} />
              <Stat
                label="Disagreement"
                value={data.total > 0 ? `${((data.corrections_count / Math.max(1, data.total)) * 100).toFixed(1)}%` : "—"}
                sub="corrections / labelled"
              />
            </div>
          </div>

          {/* ── Distribution ─────────────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="card p-5">
              <SectionTitle>What the model found</SectionTitle>
              <Distribution rows={data.by_label} />
            </div>
            <div className="card p-5">
              <SectionTitle>Who decided</SectionTitle>
              <div className="space-y-2 mt-3">
                {data.by_kind.map((k) => (
                  <div key={k.source_kind} className="flex items-center justify-between text-[12.5px]">
                    <span className="text-slate-300">{KIND_TEXT[k.source_kind] ?? k.source_kind}</span>
                    <span className="tabular-nums text-slate-400">{k.count.toLocaleString()}</span>
                  </div>
                ))}
                {data.by_kind.length === 0 && <div className="text-slate-600 text-[12.5px]">Nothing labelled yet.</div>}
              </div>
              <p className="text-[11px] text-slate-600 mt-4 leading-relaxed">
                Order of decision per listing: a <b className="text-slate-400">taught phrase</b> wins outright, then{" "}
                <b className="text-slate-400">Gemini</b> reads the text with the pattern hint attached. The{" "}
                <b className="text-slate-400">pattern fallback</b> is only stored when Gemini was unavailable and is retried. A{" "}
                <b className="text-slate-400">human</b> verdict is never overwritten.
              </p>
            </div>
          </div>

          {/* ── Memory: phrases + corrections ───────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="card overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <SectionTitle>Taught phrases</SectionTitle>
                <p className="text-[11.5px] text-slate-500 mt-1">
                  Wording a teammate marked as proof. Positive labels apply instantly without a model call; “nothing relevant” phrases are shown to Gemini as wording to ignore.
                </p>
              </div>
              {data.phrases.length === 0 ? (
                <div className="px-5 pb-5 text-[12.5px] text-slate-600">
                  Nothing taught yet — use “Wrong label?” on a Fresh card and fill in the phrase.
                </div>
              ) : (
                <ul className="divide-y divide-white/5 border-t border-white/5">
                  {data.phrases.map((p) => (
                    <li key={p.id} className="px-5 py-2.5 flex items-start gap-3">
                      <LabelPill label={p.label} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-slate-200 break-words">“{p.phrase}”</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {p.hit_count} hit{p.hit_count === 1 ? "" : "s"} · {p.created_by ?? "—"} · {ago(p.created_at)}
                          {p.url && (
                            <>
                              {" · "}
                              <a href={p.url} target="_blank" rel="noreferrer" className="text-accent-400 hover:text-accent-500">source listing ↗</a>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => void retire(p.id)}
                        disabled={retiring === p.id}
                        className="text-[11px] text-slate-500 hover:text-rose-300 shrink-0"
                        title="Stop using this phrase"
                      >
                        {retiring === p.id ? "…" : "Retire"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <SectionTitle>Corrections the model learns from</SectionTitle>
                <p className="text-[11.5px] text-slate-500 mt-1">
                  Every human overrule keeps the quote Gemini had used as proof. The latest {data.job.fewshot_n} go into the prompt as “mistakes not to repeat”.
                </p>
              </div>
              {data.corrections.length === 0 ? (
                <div className="px-5 pb-5 text-[12.5px] text-slate-600">No corrections yet.</div>
              ) : (
                <ul className="divide-y divide-white/5 border-t border-white/5 max-h-[28rem] overflow-y-auto">
                  {data.corrections.map((c) => (
                    <li key={c.id} className="px-5 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap text-[12px]">
                        {c.confirmed ? (
                          <>
                            <LabelPill label={c.new_label} />
                            <span className="text-emerald-400/90">confirmed</span>
                          </>
                        ) : (
                          <>
                            <LabelPill label={c.previous_label} />
                            <span className="text-slate-600">→</span>
                            <LabelPill label={c.new_label} />
                          </>
                        )}
                        <span className="text-slate-500 ml-auto">{c.by ?? "—"} · {ago(c.created_at)}</span>
                      </div>
                      {c.previous_evidence && !c.confirmed && (
                        <div className="text-[12px] text-slate-400 mt-1">
                          model quoted <span className="italic text-slate-300">“{c.previous_evidence}”</span>
                        </div>
                      )}
                      {c.phrase && (
                        <div className="text-[12px] text-slate-400 mt-0.5">
                          taught <span className="text-slate-300">“{c.phrase}”</span>
                        </div>
                      )}
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-[11px] text-accent-400 hover:text-accent-500">
                        {c.source === "mh" ? "MyHome" : "ss.ge"} #{c.external_id} ↗
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ── Recent verdicts ──────────────────────────────────────── */}
          <div className="card overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <SectionTitle>Latest verdicts</SectionTitle>
              <p className="text-[11.5px] text-slate-500 mt-1">The most recent labelled listings (excluding “nothing relevant”), with the quote the model relied on.</p>
            </div>
            <div className="overflow-x-auto border-t border-white/5">
              <table className="w-full text-[12.5px]">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-500">
                  <tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold">
                    <th>Label</th><th>Listing</th><th>Evidence</th><th>Reason</th><th className="!text-right">Conf.</th><th>By</th><th className="!text-right">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.recent.map((v) => (
                    <tr key={`${v.source}-${v.external_id}`} className="[&>td]:px-4 [&>td]:py-2 [&>td]:align-top hover:bg-white/[0.02]">
                      <td><LabelPill label={v.label} /></td>
                      <td className="max-w-[16rem]">
                        <a href={v.url} target="_blank" rel="noreferrer" className="text-slate-200 hover:text-accent-400 line-clamp-2" title={v.title ?? ""}>
                          {v.title || `${v.source === "mh" ? "MyHome" : "ss.ge"} #${v.external_id}`}
                        </a>
                      </td>
                      <td className="max-w-[18rem] text-slate-300 italic">{v.evidence ? `“${v.evidence}”` : <span className="text-slate-600 not-italic">—</span>}</td>
                      <td className="max-w-[18rem] text-slate-400">{v.reason ?? "—"}</td>
                      <td className="text-right tabular-nums text-slate-400">{v.confidence != null ? `${Math.round(v.confidence * 100)}%` : "—"}</td>
                      <td className="text-slate-400 whitespace-nowrap">
                        {v.source_kind === "human" ? (v.overridden_by ?? "Human") : v.source_kind === "gemini" ? (v.model ?? "Gemini") : KIND_TEXT[v.source_kind] ?? v.source_kind}
                      </td>
                      <td className="text-right text-slate-500 whitespace-nowrap">{ago(v.labelled_at)}</td>
                    </tr>
                  ))}
                  {data.recent.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-600">Nothing yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Prompt ───────────────────────────────────────────────── */}
          <div className="card overflow-hidden">
            <button
              type="button"
              onClick={() => setShowPrompt((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <div>
                <SectionTitle>What Gemini is told</SectionTitle>
                <p className="text-[11.5px] text-slate-500 mt-1">
                  The exact instructions sent with every batch, including the taught phrases and corrections above. Listing text is passed as data the model is told never to obey.
                </p>
              </div>
              <span className="text-slate-500 text-[12px] shrink-0 ml-4">{showPrompt ? "Hide" : "Show"}</span>
            </button>
            {showPrompt && (
              <pre className="border-t border-white/5 px-5 py-4 text-[11.5px] leading-relaxed text-slate-300 whitespace-pre-wrap break-words font-mono bg-black/20 max-h-[36rem] overflow-y-auto">
                {data.prompt_preview}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13.5px] font-semibold text-slate-100">{children}</h2>;
}

function Stat({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className="px-5 py-3.5">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-[20px] font-semibold tabular-nums mt-0.5 ${warn ? "text-amber-300" : "text-slate-100"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Distribution({ rows }: { rows: LabelCount[] }) {
  const total = rows.reduce((a, r) => a + r.count, 0);
  if (total === 0) return <div className="text-slate-600 text-[12.5px] mt-3">Nothing labelled yet.</div>;
  const order = ["no_agents", "likely_agent", "agents_welcome", "none"];
  const sorted = [...rows].sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
  return (
    <div className="mt-3 space-y-3">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/5">
        {sorted.map((r) => (
          <div
            key={r.label}
            className={`${LABEL_BAR[r.label] ?? "bg-slate-500"} h-full`}
            style={{ width: `${(r.count / total) * 100}%` }}
            title={`${LABEL_TEXT[r.label] ?? r.label}: ${r.count}`}
          />
        ))}
      </div>
      <div className="space-y-1.5">
        {sorted.map((r) => (
          <div key={r.label} className="flex items-center gap-2 text-[12.5px]">
            <span className={`h-2 w-2 rounded-full ${LABEL_BAR[r.label] ?? "bg-slate-500"}`} />
            <span className="text-slate-300 flex-1">{LABEL_TEXT[r.label] ?? r.label}</span>
            <span className="text-slate-500 tabular-nums">{((r.count / total) * 100).toFixed(0)}%</span>
            <span className="text-slate-400 tabular-nums w-14 text-right">{r.count.toLocaleString()}</span>
            <span className="text-slate-600 tabular-nums w-16 text-right" title="average confidence">
              {r.avg_confidence != null ? `~${Math.round(r.avg_confidence * 100)}%` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

